/**
 * LinkExistingPaymentForm — operator search for a payment by provider
 * reference, used when the customer hands over a transaction id and ops
 * needs to confirm it's already in the system before deciding whether to
 * "Record manual payment" instead.
 *
 * Flow:
 *   1. Operator types the reference (M-Pesa tx id, bank ref, etc.).
 *   2. GET /api/payments/by-reference returns 0..N matches across all
 *      channels (capped at 10 by the repo).
 *   3. UI surfaces each match with a status badge:
 *        - "Already on this loan" — green, idempotent confirmation.
 *        - "On another loan" — red, with a cross-link so ops can
 *          investigate; intentionally does NOT offer a one-click move
 *          (cross-loan reassignment needs maker-checker, which we'll
 *          add as a separate audited action).
 *   4. When zero matches: an inline hint pointing ops at "Record manual
 *      payment" / "Record deposit" instead.
 *
 * Server is read-only. No state mutations happen here — this is purely
 * confirmation / discovery. The actual settle pipeline already ran for
 * any returned row (a payment row only exists once it's been recorded).
 */
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import type { Payment } from "@dfp/api-client";
import { Button } from "./Button.js";
import { Field, FormError, TextInput, apiErrorMessage } from "./Form.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";

interface Props {
  readonly loanId: string;
}

export function LinkExistingPaymentForm(props: Props): JSX.Element | null {
  const auth = useAuth();
  const canRead = (auth.user?.permissions ?? []).includes("payment.read");
  const [open, setOpen] = useState(false);
  const [reference, setReference] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<readonly Payment[] | null>(null);

  const search = useMutation({
    mutationFn: (q: string) =>
      api.get<readonly Payment[]>(
        `/api/payments/by-reference?providerReference=${encodeURIComponent(q)}`,
      ),
    onSuccess: (rows) => setResults(rows),
    onError: (err: unknown) => {
      setError(apiErrorMessage(err));
      setResults(null);
    },
  });

  function submit(): void {
    setError(null);
    setResults(null);
    const q = reference.trim();
    if (q.length < 4) {
      setError("Reference must be at least 4 characters.");
      return;
    }
    search.mutate(q);
  }

  if (!canRead) return null;

  if (!open) {
    return <Button onClick={() => setOpen(true)}>Link existing payment</Button>;
  }

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3">
      <p className="text-sm font-medium text-slate-700">Link existing payment</p>
      <p className="text-xs text-slate-500">
        Search by provider reference (M-Pesa transaction id, bank ref, cash receipt #). If the
        payment is already in the system, you'll see it below — there's nothing more to do. If it
        isn't, use "Record installment payment" or "Record deposit" to create it.
      </p>

      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <Field label="Reference" hint="≥ 4 chars">
            <TextInput
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submit();
              }}
            />
          </Field>
        </div>
        <Button onClick={submit} disabled={search.isPending}>
          {search.isPending ? "Searching…" : "Search"}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setReference("");
            setResults(null);
            setError(null);
            setOpen(false);
          }}
        >
          Cancel
        </Button>
      </div>

      <FormError message={error} />

      {results !== null ? (
        <div className="space-y-2">
          {results.length === 0 ? (
            <div className="rounded-md bg-slate-100 border border-slate-200 px-3 py-2 text-xs text-slate-700">
              No payment with reference <span className="font-mono">{reference.trim()}</span> in the
              system. Use <span className="font-semibold">Record installment payment</span> or{" "}
              <span className="font-semibold">Record deposit</span> to create one.
            </div>
          ) : (
            <ul className="space-y-2">
              {results.map((p) => (
                <PaymentMatch key={p.id} payment={p} currentLoanId={props.loanId} />
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

function PaymentMatch({
  payment,
  currentLoanId,
}: {
  readonly payment: Payment;
  readonly currentLoanId: string;
}): JSX.Element {
  const sameLoan = payment.loanId === currentLoanId;
  return (
    <li
      className={
        "rounded-md border px-3 py-2 text-xs " +
        (sameLoan
          ? "bg-green-50 border-green-200 text-green-800"
          : "bg-red-50 border-red-200 text-red-800")
      }
    >
      <div className="flex items-center justify-between gap-3">
        <div className="space-y-0.5">
          <div className="font-semibold">
            {sameLoan ? "✓ Already on this loan" : "⚠ Found on a different loan"}
          </div>
          <div className="text-[11px] opacity-80">
            ref <span className="font-mono">{payment.providerReference}</span> · ch{" "}
            <span className="font-mono">{payment.channel}</span> · status{" "}
            <span className="font-mono">{payment.status}</span>
          </div>
        </div>
        {!sameLoan ? (
          <Link
            to="/loans/$id"
            params={{ id: payment.loanId }}
            className="text-[11px] font-semibold underline shrink-0"
          >
            Open loan {payment.loanId.slice(0, 8)}…
          </Link>
        ) : null}
      </div>
    </li>
  );
}
